import { SortModelItem } from '@ag-grid-community/core';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ResponsiveGridState } from './types';

type VersionedState = ResponsiveGridState & { version: number };

type UseResponsiveGridStateOptions = {
    initialState?: {
        filterModel?: Record<string, unknown>;
        sortModel?: SortModelItem[];
    };
};

export function useResponsiveGridState(options?: UseResponsiveGridStateOptions) {
    const [versionedState, setVersionedState] = useState<VersionedState>(() => ({
        sortModel: options?.initialState?.sortModel ?? [],
        filterModel: options?.initialState?.filterModel ?? {},
        version: 0,
    }));

    const initialAppliedRef = useRef(false);

    const stateVersion = versionedState.version;
    const state: ResponsiveGridState = useMemo(
        () => ({ sortModel: versionedState.sortModel, filterModel: versionedState.filterModel }),
        [versionedState.sortModel, versionedState.filterModel],
    );

    const updateSortModel = useCallback((sortModel: SortModelItem[]) => {
        setVersionedState((prev) => {
            if (JSON.stringify(prev.sortModel) === JSON.stringify(sortModel)) {
                return prev;
            }
            return { ...prev, sortModel, version: prev.version + 1 };
        });
    }, []);

    const updateFilterModel = useCallback((filterModel: Record<string, unknown>) => {
        setVersionedState((prev) => {
            if (JSON.stringify(prev.filterModel) === JSON.stringify(filterModel)) {
                return prev;
            }
            return { ...prev, filterModel, version: prev.version + 1 };
        });
    }, []);

    const updateFilter = useCallback((colId: string, value: unknown) => {
        setVersionedState((prev) => {
            const next = { ...prev.filterModel };
            if (value === undefined || value === null || value === '') {
                if (!(colId in prev.filterModel)) {
                    return prev;
                }
                delete next[colId];
            } else {
                if (JSON.stringify(prev.filterModel[colId]) === JSON.stringify(value)) {
                    return prev;
                }
                next[colId] = value;
            }
            return { ...prev, filterModel: next, version: prev.version + 1 };
        });
    }, []);

    const resetFiltersAndSort = useCallback(() => {
        setVersionedState((prev) => {
            if (prev.sortModel.length === 0 && Object.keys(prev.filterModel).length === 0) {
                return prev;
            }
            return { ...prev, sortModel: [], filterModel: {}, version: prev.version + 1 };
        });
    }, []);

    const markInitialApplied = useCallback(() => {
        initialAppliedRef.current = true;
    }, []);

    return {
        state,
        stateVersion,
        initialApplied: initialAppliedRef.current,
        markInitialApplied,
        updateSortModel,
        updateFilterModel,
        updateFilter,
        resetFiltersAndSort,
    };
}
